import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Conversation, Message } from '../types/Conversation';
import { FilterAiMuted, FilterChannel, FilterStatus, NavSection } from '../types/conversationNav';
import { isExternalConversation, matchesConversationNavSection } from '../utils/conversationClassification';
import { participantMatchesChannelFilter } from '../conversationChannelIcons';
import { shouldHideConversationWhenCommentsFiltered } from '../utils/instagramComposerUtils';

interface SearchResult {
  conversationId: string;
  conversationTitle: string;
  message: Message;
}

interface UseConversationSearchOptions {
  currentConversationList: Conversation[];
  navSection: NavSection;
  filterStatus: FilterStatus;
  filterAiMuted: FilterAiMuted;
  filterChannel: FilterChannel;
  filterDate: string;
  showCommentThreads: boolean;
  getConversationDisplayName: (conversation: Conversation) => string;
  onSelectConversation: (conversationId: string) => void;
}

interface UseConversationSearchResult {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  showSearchPanel: boolean;
  setShowSearchPanel: React.Dispatch<React.SetStateAction<boolean>>;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  globalSearchResults: SearchResult[];
  orderedFilteredList: Conversation[];
  onSelectSearchResult: (result: SearchResult) => void;
}

export const useConversationSearch = ({
  currentConversationList,
  navSection,
  filterStatus,
  filterAiMuted,
  filterChannel,
  filterDate,
  showCommentThreads,
  getConversationDisplayName,
  onSelectConversation,
}: UseConversationSearchOptions): UseConversationSearchResult => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState<SearchResult[]>([]);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [search]);

  const orderedFilteredList = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();

    const filteredList = currentConversationList.filter(conversation => {
      if (query && globalSearchResults.length === 0) {
        if (!(conversation.title || '').toLowerCase().includes(query) && !`${conversation.id}`.includes(query)) {
          return false;
        }
      }

      if (!matchesConversationNavSection(conversation, navSection)) return false;

      if (isExternalConversation(conversation)) {
        if (filterStatus === 'signed' && !conversation.isSigned) return false;
        if (filterStatus === 'unsigned' && conversation.isSigned) return false;
        if (filterAiMuted === 'muted' && !conversation.isAiAssistantMuted) return false;
        if (filterAiMuted === 'unmuted' && conversation.isAiAssistantMuted) return false;
      }

      if (filterChannel !== 'all') {
        const hasSelectedChannel = conversation.participants.some(participant => participantMatchesChannelFilter(participant, filterChannel));
        if (!hasSelectedChannel) return false;
      }

      if (filterDate) {
        const hasMessageOnSelectedDay = conversation.messages.some(message => {
          const timestamp = new Date(message.timestamp);
          const messageLocalDay = [
            timestamp.getFullYear(),
            String(timestamp.getMonth() + 1).padStart(2, '0'),
            String(timestamp.getDate()).padStart(2, '0'),
          ].join('-');

          return messageLocalDay === filterDate;
        });

        if (!hasMessageOnSelectedDay) return false;
      }

      if (shouldHideConversationWhenCommentsFiltered(conversation, showCommentThreads)) {
        return false;
      }

      return true;
    });

    return filteredList;
  }, [currentConversationList, debouncedSearch, filterAiMuted, filterChannel, filterDate, filterStatus, globalSearchResults.length, navSection, showCommentThreads]);

  useEffect(() => {
    if (debouncedSearch.trim().length < 2) {
      setGlobalSearchResults([]);
      setShowSearchPanel(false);
      return;
    }

    const query = debouncedSearch.toLowerCase();
    const results: SearchResult[] = [];

    currentConversationList.forEach(conversation => {
      conversation.messages.forEach(message => {
        if (message.content.toLowerCase().includes(query)) {
          results.push({
            conversationId: conversation.id,
            conversationTitle: getConversationDisplayName(conversation),
            message,
          });
        }
      });
    });

    setGlobalSearchResults(results);
    setShowSearchPanel(results.length > 0);
  }, [currentConversationList, debouncedSearch, getConversationDisplayName]);

  const onSelectSearchResult = useCallback((result: SearchResult) => {
    onSelectConversation(result.conversationId);
    setTimeout(() => {
      const element = document.getElementById(`msg-${result.message.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
    setShowSearchPanel(false);
    setSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [onSelectConversation]);

  return {
    search,
    setSearch,
    showSearchPanel,
    setShowSearchPanel,
    searchInputRef,
    globalSearchResults,
    orderedFilteredList,
    onSelectSearchResult,
  };
};